import React, { useState } from "react";
import { Square } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { RerunStatus } from "../../api/types";
import { RunDialogShell, cancelButtonClass, dangerButtonClass } from "./runDialog";

type RerunProgressDialogProps = {
  rerun: RerunStatus;
  onClose: () => void;
  /** Stops the running analysis; absent when the viewer may not stop it. */
  onCancel?: () => Promise<void>;
};

/**
 * The window onto a re-analysis running behind an already displayed result:
 * how far it has got, and the way to stop it.
 *
 * The page itself keeps showing the previous run's result while this happens,
 * so this dialog is the only place that progress is visible at all.
 */
const RerunProgressDialog = ({ rerun, onClose, onCancel }: RerunProgressDialogProps) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const progress = rerun.status === "running" ? rerun.progress : null;
  const percent = progress
    ? Math.min(
        100,
        Math.max(0, Math.round((progress.stage / Math.max(progress.stageTotal, 1)) * 100)),
      )
    : 0;

  const stop = async () => {
    if (!onCancel) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onCancel();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("failed_to_stop_analysis"));
      setBusy(false);
    }
  };

  return (
    <RunDialogShell
      labelledBy="analysis-rerun-progress-title"
      dismissLabel={t("close")}
      onDismiss={onClose}
    >
      <h2 id="analysis-rerun-progress-title" className="text-lg font-semibold">
        {rerun.status === "running" ? t("rerun_progress_title") : t("rerun_failed")}
      </h2>
      {rerun.status === "running" ? (
        <>
          <p className="mt-2 text-sm leading-6 text-text-mid">{t("rerun_progress_message")}</p>
          {progress ? (
            <div className="mt-4">
              <div className="flex items-center justify-between gap-3 text-xs text-text-dim">
                <span className="truncate">
                  {`Stage ${progress.stage} of ${progress.stageTotal}`}
                </span>
                <span className="shrink-0">{percent}%</span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted-surface">
                <div
                  className="h-full rounded-full bg-info transition-all"
                  style={{ width: `${percent}%` }}
                />
              </div>
              <p className="mt-2 truncate text-sm text-text-mid">{progress.message}</p>
            </div>
          ) : (
            <p className="mt-4 text-sm text-text-mid">{t("rerun_progress_waiting")}</p>
          )}
        </>
      ) : (
        <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-danger-strong">
          {rerun.error_message}
        </p>
      )}
      {error && <p className="mt-4 text-sm text-danger">{error}</p>}
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={onClose} className={cancelButtonClass}>
          {t("close")}
        </button>
        {rerun.status === "running" && onCancel && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void stop()}
            className={`inline-flex items-center gap-2 ${dangerButtonClass}`}
          >
            <Square size={14} aria-hidden="true" />
            {busy ? t("stopping_analysis") : t("stop_analysis")}
          </button>
        )}
      </div>
    </RunDialogShell>
  );
};

export default RerunProgressDialog;
